let cartDrawerScrollY = 0;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    cartDrawerScrollY = window.scrollY;
    document.body.style.top = `-${cartDrawerScrollY}px`;

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);

    document.body.classList.remove('overflow-hidden');

    // ✅ RESTORE SCROLL POSITION
    document.body.style.top = '';
    window.scrollTo(0, cartDrawerScrollY);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


class CartEditItem extends HTMLElement {
  constructor() {
    super();
    this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    this.displayContainer = this.querySelector('.variant-display');
    this.editContainer = this.querySelector('.variant-edit');
    
    // Bind selects to check availability
    this.querySelectorAll('select').forEach(select => {
      select.addEventListener('change', () => this.checkVariantAvailability());
    });
  }

  toggleEdit() {
    const isHidden = this.editContainer.style.display === 'none';
    this.editContainer.style.display = isHidden ? 'block' : 'none';
    this.displayContainer.style.display = isHidden ? 'none' : 'block';
  }

  getCurrentOptions() {
    // Collects currently selected values from dropdowns
    return Array.from(this.querySelectorAll('select')).map(select => select.value);
  }

  checkVariantAvailability() {
    const currentOptions = this.getCurrentOptions();
    
    // Find matching variant
    const matchedVariant = this.variants.find(variant => {
      return variant.options.every((option, index) => option === currentOptions[index]);
    });

    const updateBtn = this.querySelector('.update-btn');
    
    if (matchedVariant && matchedVariant.available) {
      updateBtn.disabled = false;
      updateBtn.textContent = 'Update';
      this.dataset.targetVariantId = matchedVariant.id;
    } else {
      updateBtn.disabled = true;
      updateBtn.textContent = matchedVariant ? 'Sold Out' : 'Unavailable';
    }
  }

  updateVariant() {
    const newVariantId = this.dataset.targetVariantId;
    const originalLineKey = this.dataset.lineKey;
    const quantity = parseInt(this.dataset.quantity);

    if (!newVariantId || newVariantId === this.dataset.variantId) {
      this.toggleEdit(); // No change made
      return;
    }

    const body = JSON.stringify({
      updates: {
        [originalLineKey]: 0, // Remove old item
        [newVariantId]: quantity // Add new item (Note: this might merge if item exists)
      }
    });
    
    // Using the 'updates' endpoint allows doing both in one request
    // However, to be safe with indexes, we use the /cart/update.js logic
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body
    })
    .then(response => response.json())
    .then(parsedState => {
      // Refresh the cart drawer
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.renderContents(parsedState);
      } else {
        window.location.reload();
      }
    })
    .catch(e => console.error(e));
  }
}

customElements.define('cart-edit-item', CartEditItem);